import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { getStickers, getProductStickers } from '../services/stickerService';
import { getCategories } from '../services/categoryService';

const StickerContext = createContext({
  stickers: [],
  categoryTree: [],
  loading: false,
  getStickersForProduct: () => [],
  refreshStickers: async () => []
});

export function StickerProvider({ children }) {
  const [stickers, setStickers] = useState([]);
  const [categoryTree, setCategoryTree] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchStickersAndCategories = useCallback(async (force = false) => {
    try {
      setLoading(true);
      const [stkData, catData] = await Promise.all([
        getStickers(force).catch(() => []),
        getCategories().catch(() => [])
      ]);
      setStickers(Array.isArray(stkData) ? stkData : []);
      if (Array.isArray(catData)) {
        setCategoryTree(catData);
      }
      return stkData;
    } catch (err) {
      console.error('Failed to load stickers or categories:', err);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStickersAndCategories(false);

    const handleUpdate = (e) => {
      if (e.detail?.stickers) {
        setStickers(e.detail.stickers);
      } else {
        fetchStickersAndCategories(true);
      }
    };

    window.addEventListener('stickersUpdated', handleUpdate);
    return () => {
      window.removeEventListener('stickersUpdated', handleUpdate);
    };
  }, [fetchStickersAndCategories]);

  const getStickersForProduct = useCallback((product) => {
    return getProductStickers(product, stickers, categoryTree);
  }, [stickers, categoryTree]);

  const value = useMemo(() => ({
    stickers,
    categoryTree,
    loading,
    getStickersForProduct,
    refreshStickers: () => fetchStickersAndCategories(true)
  }), [stickers, categoryTree, loading, getStickersForProduct, fetchStickersAndCategories]);

  return (
    <StickerContext.Provider value={value}>
      {children}
    </StickerContext.Provider>
  );
}

export function useStickers() {
  return useContext(StickerContext);
}

export default StickerContext;
